import React, { useEffect, useState } from "react";
import {
  Lock, Loader2, LogOut, RefreshCw, CheckCircle2, Download, BadgePoundSterling,
  ShoppingBag, GraduationCap, FileText, Mail, AlertCircle, BookOpenCheck, Video,
  Calendar, Phone, ClipboardList, Award, Plus, Trash2, Image as ImageIcon,
  Edit3, Eye, EyeOff, X, Copy, Truck, Save, BarChart3, Users, Globe, TrendingUp,
  Mic, UserPlus, UserMinus, ArrowLeftRight, Package, Bell,
} from "lucide-react";
import {
  adminLogin, adminDashboard, adminMarkCertificateSent, adminAnalytics, adminRefundOrder, adminMarkDelivered,
  adminExamBookings, adminExamSchedule, adminExamResult,
  adminSessionStats, adminListSessions, adminSetSessionStatus, adminCancelSession, adminRescheduleSession, adminNotificationLogs,
  listShopProducts, adminShopCreate, adminShopUpdate, adminShopDelete, adminShopUploadImage,
  getAdminToken, setAdminToken, clearAdminToken, API, shopImageUrl,
} from "../lib/api";
import axios from "axios";

const PRODUCT_META = {
  illness_pdf: { label: "Illness PDF", icon: FileText, color: "#2C5545" },
  diploma_course: { label: "Diploma Course", icon: GraduationCap, color: "#B68D40" },
  exam_booking: { label: "Exam Booking", icon: Video, color: "#3A6E5A" },
  diploma_certificate: { label: "Diploma Certificate", icon: Award, color: "#B68D40" },
  hijama_atlas_book: { label: "Hijama Atlas Book", icon: BookOpenCheck, color: "#8C3A3A" },
  shop_item: { label: "Shop Item", icon: ShoppingBag, color: "#2C5545" },
};

const STATUS_BADGE = {
  paid: "bg-forest/10 text-forest",
  initiated: "bg-mute/15 text-mute",
  expired: "bg-clay/10 text-clay",
  failed: "bg-clay/10 text-clay",
};

const fmtMoney = (n, ccy = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const copyToClipboard = (txt) => {
  try { navigator.clipboard.writeText(txt); return true; } catch { return false; }
};

// ============================================ Login ============================================
const AdminLogin = ({ onLoggedIn }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault(); setError(""); setLoading(true);
    try {
      const r = await adminLogin(password);
      if (r?.token) { setAdminToken(r.token); onLoggedIn(); } else throw new Error("Login failed");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Login failed"); setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md clay-surface rounded-3xl p-8 sm:p-10">
        <div className="text-center mb-7">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-forest/10 items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-forest" />
          </div>
          <h1 className="font-heading text-3xl text-forest font-bold mb-1">Clinic Admin</h1>
          <p className="text-mute text-sm">Sign in to manage orders, exams, certificates & shop</p>
        </div>
        <label className="block text-xs uppercase tracking-widest text-mute mb-2">Admin password</label>
        <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus autoComplete="current-password"
          className="w-full rounded-2xl bg-cream border border-forest/10 px-5 py-3.5 text-sm text-ink outline-none focus:border-forest/40 transition mb-4" placeholder="••••••••" />
        {error && <p data-testid="admin-login-error" className="text-xs text-clay bg-clay/10 rounded-lg px-3 py-2 mb-4">{error}</p>}
        <button data-testid="admin-login-submit" type="submit" disabled={loading || !password}
          className="w-full inline-flex items-center justify-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-7 py-3.5 text-sm font-medium transition disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Sign in
        </button>
      </form>
    </div>
  );
};

const SessionsTab = () => {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    try {
      const [st, ls] = await Promise.all([adminSessionStats(), adminListSessions()]);
      setStats(st); setSessions(ls.sessions || []);
    } catch (e) { console.debug("sessions load failed", e); }
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    setBusy(`${id}-${status}`);
    try { await adminSetSessionStatus(id, status); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const cancelSession = async (id) => {
    if (!window.confirm("Cancel this session? The student and teacher will be notified on WhatsApp + email, and you'll get an admin alert.")) return;
    const reason = window.prompt("Reason for cancellation (optional):", "") || "";
    setBusy(`${id}-cancel`);
    try { await adminCancelSession(id, reason); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const rescheduleSession = async (id) => {
    const local = window.prompt("New session date & time (your local time, format: YYYY-MM-DD HH:MM):", "");
    if (!local) return;
    const dt = new Date(local.replace(" ", "T"));
    if (isNaN(dt.getTime())) { alert("Could not read that date/time. Use YYYY-MM-DD HH:MM"); return; }
    const reason = window.prompt("Reason for the change (optional):", "") || "";
    setBusy(`${id}-reschedule`);
    try { await adminRescheduleSession(id, dt.toISOString(), reason, 0); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  return (
    <div data-testid="sessions-tab" className="space-y-8">
      {/* Per-teacher completed counts for monthly settlement */}
      <div className="clay-surface rounded-3xl overflow-hidden">
        <div className="px-5 py-3 bg-forest/5 text-forest text-xs uppercase tracking-widest">Completed sessions per teacher (monthly settlement)</div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-mute"><tr>
            <th className="text-left px-5 py-2">Teacher</th><th className="px-5 py-2">Completed</th>
            <th className="px-5 py-2">Missed</th><th className="px-5 py-2">Failed</th><th className="px-5 py-2">Scheduled</th><th className="px-5 py-2">Total</th>
          </tr></thead>
          <tbody>
            {stats?.by_teacher?.length ? stats.by_teacher.map((t) => (
              <tr key={t.tutor} className="border-t border-forest/5 text-center">
                <td className="text-left px-5 py-2 text-ink">{t.tutor}</td>
                <td className="px-5 py-2 text-forest font-semibold">{t.completed}</td>
                <td className="px-5 py-2">{t.missed}</td><td className="px-5 py-2">{t.failed}</td>
                <td className="px-5 py-2">{t.scheduled}</td><td className="px-5 py-2">{t.total}</td>
              </tr>
            )) : <tr><td colSpan={6} className="text-center py-6 text-mute">No paid sessions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Session list with status controls + alerts */}
      <div className="space-y-3">
        {sessions.map((s) => (
          <div key={s.id} data-testid={`admin-session-${s.id}`} className="clay-surface rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-ink font-medium">{s.subject} · <span className="text-mute">{s.tutor_name}</span></p>
              <p className="text-xs text-mute">{s.student_name} ({s.student_email}) · {(s.sessions?.[0]?.starts_at) ? new Date(s.sessions[0].starts_at).toLocaleString() : "?"}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest bg-forest/10 text-forest px-2.5 py-1 rounded-full">{s.status}</span>
              {["completed", "missed", "failed"].map((st) => (
                <button key={st} data-testid={`session-${st}-${s.id}`} onClick={() => setStatus(s.id, st)} disabled={busy === `${s.id}-${st}`}
                  className={`text-[11px] px-3 py-1.5 rounded-full border transition ${st === "completed" ? "border-forest/30 text-forest hover:bg-forest hover:text-sand" : st === "missed" ? "border-gold/40 text-gold hover:bg-gold hover:text-forest" : "border-clay/40 text-clay hover:bg-clay hover:text-sand"}`}>
                  {busy === `${s.id}-${st}` ? "…" : st}
                </button>
              ))}
              <button data-testid={`session-reschedule-${s.id}`} onClick={() => rescheduleSession(s.id)} disabled={busy === `${s.id}-reschedule`}
                className="text-[11px] px-3 py-1.5 rounded-full border border-forest/30 text-forest hover:bg-forest hover:text-sand transition">
                {busy === `${s.id}-reschedule` ? "…" : "reschedule"}
              </button>
              <button data-testid={`session-cancel-${s.id}`} onClick={() => cancelSession(s.id)} disabled={busy === `${s.id}-cancel`}
                className="text-[11px] px-3 py-1.5 rounded-full border border-clay/40 text-clay hover:bg-clay hover:text-sand transition">
                {busy === `${s.id}-cancel` ? "…" : "cancel"}
              </button>
            </div>
          </div>
        ))}
        {sessions.length === 0 && <p className="text-center text-mute text-sm py-6">No sessions booked yet.</p>}
      </div>
    </div>
  );
};


// ====================== Top bar shared by all dashboard tabs =======================
const AdminBar = ({ tab, setTab, onLogout, onRefresh, loading }) => (
  <div className="border-b border-forest/10 bg-white/80 backdrop-blur sticky top-0 z-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-forest text-sand flex items-center justify-center"><Lock className="w-4 h-4" /></div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-mute">Islamic Cure & Care</p>
          <h1 className="font-heading text-xl text-forest font-semibold leading-none">Clinic Admin</h1>
        </div>
      </div>
      <div className="flex items-center gap-1 bg-cream rounded-full p-1">
        {[
          { id: "overview", label: "Overview", icon: BadgePoundSterling },
          { id: "analytics", label: "Analytics", icon: BarChart3 },
          { id: "exams", label: "Exam Bookings", icon: Video },
          { id: "shop", label: "Shop", icon: ShoppingBag },
          { id: "tutors", label: "Tutors", icon: GraduationCap },
          { id: "sessions", label: "Sessions", icon: Video },
          { id: "notifications", label: "Notifications", icon: Bell },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-medium transition ${tab === id ? "bg-forest text-sand shadow-sm" : "text-mute hover:text-forest"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="admin-refresh" onClick={onRefresh}
          className="inline-flex items-center gap-1.5 text-xs text-forest hover:bg-forest/5 rounded-full px-3 py-2 transition">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button data-testid="admin-logout" onClick={onLogout}
          className="inline-flex items-center gap-1.5 text-xs text-clay hover:bg-clay/10 rounded-full px-3 py-2 transition">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </div>
  </div>
);

// =========================== Analytics tab ===============================
const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await adminAnalytics();
        if (alive) setData(d);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || "Could not load analytics");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>
  );
  if (err) return (
    <div className="p-6 rounded-2xl bg-clay/10 text-clay border border-clay/20 max-w-xl mx-auto mt-6">{err}</div>
  );
  if (!data) return null;

  const { summary, daily_chart, top_illnesses, top_pages, languages } = data;
  const langLabels = { en: "English", ar: "Arabic", ur: "Urdu", de: "German", fr: "French" };

  // tiny inline SVG sparkline for the last 30 days
  const maxV = Math.max(1, ...daily_chart.map((d) => d.views));
  const w = 600, h = 90, pad = 6;
  const points = daily_chart.length > 1 ? daily_chart.map((d, i) => {
    const x = pad + (i * (w - 2 * pad)) / Math.max(1, daily_chart.length - 1);
    const y = h - pad - (d.views / maxV) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ") : "";

  const KPI = ({ label, value, icon: Icon, color = "#2C5545" }) => (
    <div className="clay-surface rounded-2xl p-5" style={{ borderTop: `3px solid ${color}` }}>
      <Icon className="w-5 h-5 text-gold mb-2" />
      <p className="text-3xl font-heading text-forest font-semibold leading-none">{value.toLocaleString()}</p>
      <p className="text-xs text-mute mt-1">{label}</p>
    </div>
  );

  return (
    <div data-testid="analytics-tab" className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl text-forest mb-1">Analytics</h2>
        <p className="text-sm text-mute">Privacy-friendly page views and unique visitors. No IPs collected.</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpi">
        <KPI label="Page views today" value={summary.views_today} icon={Eye} color="#B68D40" />
        <KPI label="Page views (7 days)" value={summary.views_7d} icon={TrendingUp} color="#2C5545" />
        <KPI label="Visitors today" value={summary.visitors_today} icon={Users} color="#B68D40" />
        <KPI label="Visitors (30 days)" value={summary.visitors_30d} icon={Users} color="#2C5545" />
      </div>

      {/* Daily chart */}
      <div className="clay-surface rounded-2xl p-6">
        <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-3">Daily Page Views — last 30 days</p>
        {daily_chart.length === 0 ? (
          <p className="text-sm text-mute italic">No data yet. Once visitors arrive, the chart will appear here.</p>
        ) : (
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
            <polyline fill="none" stroke="#2C5545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points} />
            {daily_chart.map((d, i) => {
              const x = pad + (i * (w - 2 * pad)) / Math.max(1, daily_chart.length - 1);
              const y = h - pad - (d.views / maxV) * (h - 2 * pad);
              return <circle key={d.date} cx={x} cy={y} r="2.5" fill="#B68D40" />;
            })}
          </svg>
        )}
        {daily_chart.length > 0 && (
          <p className="text-[10px] text-mute mt-1">
            {daily_chart[0]?.date} → {daily_chart[daily_chart.length - 1]?.date}  ·  peak: {maxV.toLocaleString()} views/day
          </p>
        )}
      </div>

      {/* Top illnesses + top pages + languages */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="clay-surface rounded-2xl p-6" data-testid="top-illnesses">
          <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-3">Top Illnesses (last 30 days)</p>
          {top_illnesses.length === 0 ? (
            <p className="text-sm text-mute italic">No illness views yet.</p>
          ) : (
            <ul className="divide-y divide-forest/10">
              {top_illnesses.map((r) => (
                <li key={r.path} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-forest font-medium truncate pr-2">{r.name}</span>
                  <span className="text-gold font-semibold">{r.views.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="clay-surface rounded-2xl p-6" data-testid="languages">
          <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-3">Languages</p>
          {languages.length === 0 ? (
            <p className="text-sm text-mute italic">No data yet.</p>
          ) : (
            <ul className="divide-y divide-forest/10">
              {languages.map((r) => (
                <li key={r.lang} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-forest font-medium">{langLabels[r.lang] || r.lang}</span>
                  <span className="text-gold font-semibold">{r.views.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="clay-surface rounded-2xl p-6" data-testid="top-pages">
        <p className="text-xs uppercase tracking-wider text-clay font-semibold mb-3">Most-Viewed Pages</p>
        {top_pages.length === 0 ? (
          <p className="text-sm text-mute italic">No page views yet.</p>
        ) : (
          <ul className="divide-y divide-forest/10">
            {top_pages.map((r) => (
              <li key={r.path} className="flex items-center justify-between py-2 text-sm">
                <span className="text-forest font-mono text-xs truncate pr-2">{r.path}</span>
                <span className="text-gold font-semibold">{r.views.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-mute italic">
        Tracking is anonymous — only a random device ID stored in the visitor's own browser is used. No IPs, no fingerprinting.
      </p>
    </div>
  );
};


// =========================== Overview tab ===============================
const OverviewTab = ({ data, busy, onMarkSent, onDownloadCert, onRefund, onMarkDelivered }) => {
  const RevenueTile = ({ productKey, d }) => {
    const meta = PRODUCT_META[productKey] || { label: productKey, icon: FileText, color: "#2C5545" };
    const Icon = meta.icon;
    return (
      <div data-testid={`revenue-tile-${productKey}`} className="clay-surface rounded-2xl p-5" style={{ borderTop: `3px solid ${meta.color}` }}>
        <div className="flex items-center justify-between mb-3">
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
          <span className="text-[10px] uppercase tracking-widest text-mute">{d.count} sold</span>
        </div>
        <p className="text-xs text-mute mb-1">{meta.label}</p>
        <p className="font-heading text-3xl text-forest font-bold leading-none">{fmtMoney(d.revenue)}</p>
      </div>
    );
  };

  return (
    <>
      {/* KPI tiles */}
      <section className="mb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div data-testid="kpi-total-revenue" className="clay-surface rounded-2xl p-5">
            <BadgePoundSterling className="w-5 h-5 text-gold mb-2" />
            <p className="text-xs text-mute">Total revenue (paid)</p>
            <p className="font-heading text-3xl text-forest font-bold">{fmtMoney(data?.total_revenue || 0)}</p>
          </div>
          <div data-testid="kpi-paid-orders" className="clay-surface rounded-2xl p-5">
            <ShoppingBag className="w-5 h-5 text-forest mb-2" />
            <p className="text-xs text-mute">Paid orders</p>
            <p className="font-heading text-3xl text-forest font-bold">{data?.total_paid_orders || 0}</p>
          </div>
          <div data-testid="kpi-pending-certs" className="clay-surface rounded-2xl p-5">
            <GraduationCap className="w-5 h-5 text-clay mb-2" />
            <p className="text-xs text-mute">Pending certificates</p>
            <p className="font-heading text-3xl text-forest font-bold">{(data?.pending_certificates || []).length}</p>
          </div>
          <div data-testid="kpi-shop-pending" className="clay-surface rounded-2xl p-5">
            <Truck className="w-5 h-5 text-forest mb-2" />
            <p className="text-xs text-mute">Shop awaiting shipment</p>
            <p className="font-heading text-3xl text-forest font-bold">{(data?.shop_pending_shipments || []).length}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(data?.revenue_by_product || {}).map(([k, v]) => <RevenueTile key={k} productKey={k} d={v} />)}
          {Object.keys(data?.revenue_by_product || {}).length === 0 && (
            <div className="sm:col-span-3 text-center text-mute text-sm py-6 clay-surface rounded-2xl">No paid orders yet.</div>
          )}
        </div>
      </section>

      {/* Pending diploma certs */}
      <section className="mb-10">
        <div className="mb-4">
          <h2 className="font-heading text-2xl text-forest font-bold">Pending Diploma Certificates</h2>
          <p className="text-mute text-sm">Download → print → sign &amp; stamp → email to buyer → mark as sent.</p>
        </div>
        <div className="clay-surface rounded-3xl overflow-hidden">
          {(data?.pending_certificates || []).length === 0 ? (
            <div className="py-12 text-center text-mute text-sm"><CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-forest" />All caught up — no pending diplomas right now.</div>
          ) : (
            <table className="w-full text-sm" data-testid="pending-certs-table">
              <thead className="bg-forest/5 text-forest text-[11px] uppercase tracking-widest">
                <tr><th className="text-left px-5 py-3">Buyer</th><th className="text-left px-5 py-3">Cert ID</th><th className="text-left px-5 py-3">Paid on</th><th className="text-right px-5 py-3">Action</th></tr>
              </thead>
              <tbody>
                {data.pending_certificates.map((c) => (
                  <tr key={c.session_id} data-testid={`pending-cert-${c.session_id}`} className="border-t border-forest/5">
                    <td className="px-5 py-4">
                      <p className="text-ink font-medium">{c.full_name || "(name missing)"}</p>
                      <p className="text-xs text-mute flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {c.email || "—"}</p>
                    </td>
                    <td className="px-5 py-4 text-mute font-mono text-xs">{c.certificate_id || "—"}</td>
                    <td className="px-5 py-4 text-mute">{fmtDate(c.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex gap-2">
                        <button data-testid={`cert-download-${c.session_id}`} onClick={() => onDownloadCert(c.session_id)} disabled={busy === `dl-${c.session_id}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-forest text-sand hover:bg-sage rounded-full px-4 py-2 transition disabled:opacity-50">
                          {busy === `dl-${c.session_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Unsigned PDF
                        </button>
                        <button data-testid={`cert-mark-sent-${c.session_id}`} onClick={() => onMarkSent(c.session_id)} disabled={busy === c.session_id}
                          className="inline-flex items-center gap-1.5 text-xs bg-gold text-forest hover:bg-gold/90 rounded-full px-4 py-2 transition disabled:opacity-50">
                          {busy === c.session_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Mark as sent
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Shop awaiting shipment */}
      {(data?.shop_pending_shipments || []).length > 0 && (
        <section className="mb-10">
          <h2 className="font-heading text-2xl text-forest font-bold mb-4">Shop orders to ship</h2>
          <div className="clay-surface rounded-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-forest/5 text-forest text-[11px] uppercase tracking-widest">
                <tr><th className="text-left px-5 py-3">Buyer</th><th className="text-left px-5 py-3">Product</th><th className="text-right px-5 py-3">Amount</th><th className="text-left px-5 py-3">Paid on</th></tr>
              </thead>
              <tbody>
                {data.shop_pending_shipments.map((o) => (
                  <tr key={o.session_id} className="border-t border-forest/5">
                    <td className="px-5 py-4">
                      <p className="text-ink">{o.full_name || "—"}</p>
                      <p className="text-xs text-mute">{o.email}</p>
                      {o.phone && <p className="text-xs text-mute">{o.phone}</p>}
                    </td>
                    <td className="px-5 py-4 text-ink">{o.shop_product_snapshot?.name || "—"}</td>
                    <td className="px-5 py-4 text-right text-forest font-medium">{fmtMoney(o.amount)}</td>
                    <td className="px-5 py-4 text-mute">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Recent orders */}
      <section>
        <h2 className="font-heading text-2xl text-forest font-bold mb-4">Recent Orders</h2>
        <div className="clay-surface rounded-3xl overflow-hidden">
          {(data?.recent_orders || []).length === 0 ? <div className="py-12 text-center text-mute text-sm">No orders yet.</div> : (
            <table className="w-full text-sm" data-testid="recent-orders-table">
              <thead className="bg-forest/5 text-forest text-[11px] uppercase tracking-widest"><tr><th className="text-left px-5 py-3">When</th><th className="text-left px-5 py-3">Product</th><th className="text-left px-5 py-3">Buyer</th><th className="text-right px-5 py-3">Amount</th><th className="text-right px-5 py-3">Status</th><th className="text-right px-5 py-3">Action</th></tr></thead>
              <tbody>
                {data.recent_orders.map((o) => {
                  const meta = PRODUCT_META[o.product_type] || { label: o.product_type, icon: FileText, color: "#2C5545" };
                  const Icon = meta.icon;
                  return (
                    <tr key={o.session_id} className="border-t border-forest/5">
                      <td className="px-5 py-4 text-mute whitespace-nowrap">{fmtDate(o.created_at)}</td>
                      <td className="px-5 py-4"><span className="inline-flex items-center gap-1.5 text-xs text-ink"><Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />{meta.label}</span>{o.illness_id && <p className="text-[10px] text-mute mt-0.5 font-mono">{o.illness_id}</p>}</td>
                      <td className="px-5 py-4"><p className="text-ink">{o.full_name || "—"}</p><p className="text-xs text-mute">{o.email || "—"}</p></td>
                      <td className="px-5 py-4 text-right font-medium text-forest whitespace-nowrap">{fmtMoney(o.amount, (o.currency || "gbp").toUpperCase())}</td>
                      <td className="px-5 py-4 text-right"><span className={`inline-flex text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${STATUS_BADGE[o.payment_status] || "bg-mute/15 text-mute"}`}>{o.payment_status}</span></td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                          {o.product_type === "shop_item" && o.payment_status === "paid" && (
                            o.shop_status === "delivered" ? (
                              <span className="text-[10px] uppercase tracking-widest text-forest">Delivered ✓</span>
                            ) : (
                              <button
                                data-testid={`deliver-order-${o.session_id}`}
                                onClick={() => onMarkDelivered(o)}
                                disabled={busy === `deliver-${o.session_id}`}
                                className="inline-flex items-center gap-1.5 text-xs bg-forest/10 text-forest hover:bg-forest hover:text-sand border border-forest/30 rounded-full px-4 py-2 transition disabled:opacity-50">
                                {busy === `deliver-${o.session_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />} Delivered
                              </button>
                            )
                          )}
                          {o.payment_status === "paid" ? (
                            <button
                              data-testid={`refund-order-${o.session_id}`}
                              onClick={() => onRefund(o)}
                              disabled={busy === `refund-${o.session_id}`}
                              className="inline-flex items-center gap-1.5 text-xs bg-clay/10 text-clay hover:bg-clay hover:text-sand border border-clay/30 rounded-full px-4 py-2 transition disabled:opacity-50">
                              {busy === `refund-${o.session_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgePoundSterling className="w-3.5 h-3.5" />} Refund
                            </button>
                          ) : o.payment_status === "refunded" ? (
                            <span className="text-[10px] uppercase tracking-widest text-mute">Refunded</span>
                          ) : (
                            <span className="text-mute">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
};

// =========================== Exam bookings tab ===============================
const ExamsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState({});  // { session_id: dateString }

  const load = async () => {
    setLoading(true);
    try { const d = await adminExamBookings(); setBookings(d.bookings || []); } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSchedule = async (sid) => {
    const val = editingSchedule[sid];
    if (!val) { alert("Please pick a date/time first"); return; }
    setBusy(sid);
    try { await adminExamSchedule(sid, new Date(val).toISOString(), ""); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const handleResult = async (sid, result) => {
    if (!window.confirm(`Mark this student as ${result.toUpperCase()}?`)) return;
    setBusy(sid);
    try { const r = await adminExamResult(sid, result); if (result === "passed" && r.cert_token) {
      const url = `${window.location.origin}/certificate-purchase/${r.cert_token}`;
      if (copyToClipboard(url)) alert("Pass recorded. Certificate-purchase link copied to clipboard:\n\n" + url);
      else prompt("Pass recorded. Share this link with the student:", url);
    } else { alert("Recorded as failed."); }
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const statusBadge = (b) => {
    if (b.exam_result === "passed") return <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-forest/10 text-forest">Passed{b.cert_purchased ? " · cert paid" : ""}</span>;
    if (b.exam_result === "failed") return <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-clay/10 text-clay">Failed</span>;
    if (b.exam_status === "scheduled") return <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-gold/15 text-gold">Scheduled</span>;
    if (b.exam_status === "paid_unscheduled") return <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-sage/20 text-forest">Paid · awaiting schedule</span>;
    return <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-mute/15 text-mute">{b.exam_status || "—"}</span>;
  };

  if (loading) return <div className="py-20 text-center text-mute"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div data-testid="exams-tab">
      <div className="mb-6">
        <h2 className="font-heading text-2xl sm:text-3xl text-forest font-bold mb-1">Practical Exam Bookings</h2>
        <p className="text-mute text-sm">Schedule the video call, then record pass/fail. When you mark "passed" we copy a unique certificate-purchase link to your clipboard — send it to the student.</p>
      </div>
      <div className="clay-surface rounded-3xl overflow-hidden">
        {bookings.length === 0 ? <div className="py-12 text-center text-mute text-sm">No exam bookings yet.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-forest/5 text-forest text-[11px] uppercase tracking-widest">
              <tr><th className="text-left px-5 py-3">Student</th><th className="text-left px-5 py-3">Booked</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Schedule</th><th className="text-right px-5 py-3">Action</th></tr>
            </thead>
            <tbody>
              {bookings.map((b) => {
                const isFinal = b.exam_result === "passed" || b.exam_result === "failed";
                return (
                  <tr key={b.session_id} data-testid={`exam-row-${b.session_id}`} className="border-t border-forest/5 align-top">
                    <td className="px-5 py-4">
                      <p className="text-ink font-medium">{b.full_name}</p>
                      <p className="text-xs text-mute flex items-center gap-1"><Mail className="w-3 h-3" />{b.email}</p>
                      {b.phone && <p className="text-xs text-mute flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone}</p>}
                    </td>
                    <td className="px-5 py-4 text-mute whitespace-nowrap">{fmtDate(b.created_at)}</td>
                    <td className="px-5 py-4">{statusBadge(b)}</td>
                    <td className="px-5 py-4 min-w-[210px]">
                      {isFinal ? (
                        <span className="text-xs text-mute">{fmtDate(b.exam_scheduled_at)}</span>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          <input type="datetime-local"
                            data-testid={`exam-schedule-input-${b.session_id}`}
                            defaultValue={b.exam_scheduled_at ? new Date(b.exam_scheduled_at).toISOString().slice(0,16) : ""}
                            onChange={(e) => setEditingSchedule((s) => ({ ...s, [b.session_id]: e.target.value }))}
                            className="bg-cream rounded-lg border border-forest/15 px-3 py-1.5 text-xs" />
                          <button data-testid={`exam-schedule-save-${b.session_id}`} onClick={() => handleSchedule(b.session_id)} disabled={busy === b.session_id}
                            className="inline-flex items-center justify-center gap-1.5 text-[11px] bg-forest text-sand rounded-full px-3 py-1 hover:bg-sage disabled:opacity-50">
                            <Calendar className="w-3 h-3" /> Save schedule
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {!isFinal && (
                        <div className="inline-flex gap-2">
                          <button data-testid={`exam-pass-${b.session_id}`} onClick={() => handleResult(b.session_id, "passed")} disabled={busy === b.session_id}
                            className="inline-flex items-center gap-1.5 text-xs bg-forest text-sand hover:bg-sage rounded-full px-3.5 py-2 disabled:opacity-50">
                            {busy === b.session_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Pass
                          </button>
                          <button data-testid={`exam-fail-${b.session_id}`} onClick={() => handleResult(b.session_id, "failed")} disabled={busy === b.session_id}
                            className="inline-flex items-center gap-1.5 text-xs bg-clay/10 text-clay hover:bg-clay/20 rounded-full px-3.5 py-2 disabled:opacity-50">
                            <X className="w-3 h-3" /> Fail
                          </button>
                        </div>
                      )}
                      {b.exam_result === "passed" && b.cert_token && !b.cert_purchased && (
                        <button data-testid={`copy-cert-link-${b.session_id}`} onClick={() => {
                          const url = `${window.location.origin}/certificate-purchase/${b.cert_token}`;
                          copyToClipboard(url); alert("Link copied:\n\n" + url);
                        }} className="inline-flex items-center gap-1.5 text-xs bg-gold text-forest hover:bg-gold/90 rounded-full px-3.5 py-2">
                          <Copy className="w-3 h-3" /> Copy cert link
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// =========================== Shop tab ===============================
const ShopTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const d = await listShopProducts(true); setProducts(d.products || []); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setShowForm(true); };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try { await adminShopDelete(p.id); await load(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (p) => {
    try { await adminShopUpdate(p.id, { active: !p.active }); await load(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="shop-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl text-forest font-bold mb-1">Shop Products</h2>
          <p className="text-mute text-sm">Add products (name, price, postage, picture) — they appear on the public /products page.</p>
        </div>
        <button data-testid="shop-add-btn" onClick={openCreate} className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-5 py-2.5 text-sm font-medium">
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>

      {loading ? <div className="py-12 text-center text-mute"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
        products.length === 0 ? (
          <div className="clay-surface rounded-3xl p-10 text-center text-mute">
            <ShoppingBag className="w-6 h-6 mx-auto mb-3 text-gold" />
            <p className="text-sm mb-1">No products yet.</p>
            <p className="text-xs">Click "New product" to add your first one.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => {
              const imgSrc = p.image_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url;
              return (
                <div key={p.id} data-testid={`admin-product-${p.id}`} className="clay-surface rounded-2xl overflow-hidden">
                  <div className="aspect-[4/3] bg-cream relative overflow-hidden">
                    {imgSrc ? <img src={imgSrc} alt={p.name} className="w-full h-full object-cover" /> : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-mute/60"><ImageIcon className="w-8 h-8 mb-1" /><span className="text-xs">No image</span></div>
                    )}
                    {!p.active && <span className="absolute top-2 left-2 bg-clay text-sand text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full">Hidden</span>}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base text-forest font-semibold leading-tight mb-1">{p.name}</h3>
                    <p className="text-xs text-mute mb-2">{p.category || "—"} · {fmtMoney(p.price)} + {fmtMoney(p.postage || 0)} postage</p>
                    <div className="flex items-center gap-2">
                      <button data-testid={`shop-edit-${p.id}`} onClick={() => openEdit(p)} className="inline-flex items-center gap-1 text-[11px] bg-forest/10 text-forest hover:bg-forest/15 rounded-full px-3 py-1"><Edit3 className="w-3 h-3" /> Edit</button>
                      <button data-testid={`shop-toggle-${p.id}`} onClick={() => toggleActive(p)} className="inline-flex items-center gap-1 text-[11px] bg-gold/15 text-gold hover:bg-gold/25 rounded-full px-3 py-1">{p.active ? <><EyeOff className="w-3 h-3"/>Hide</> : <><Eye className="w-3 h-3"/>Show</>}</button>
                      <button data-testid={`shop-delete-${p.id}`} onClick={() => handleDelete(p)} className="inline-flex items-center gap-1 text-[11px] bg-clay/10 text-clay hover:bg-clay/20 rounded-full px-3 py-1"><Trash2 className="w-3 h-3" /> Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {showForm && <ShopProductForm product={editing} onClose={() => setShowForm(false)} onSaved={async () => { setShowForm(false); await load(); }} />}
    </div>
  );
};

const ShopProductForm = ({ product, onClose, onSaved }) => {
  const [name, setName] = useState(product?.name || "");
  const [arabic, setArabic] = useState(product?.arabic_name || "");
  const [category, setCategory] = useState(product?.category || "");
  const [description, setDescription] = useState(product?.description || "");
  const [price, setPrice] = useState(product?.price ?? "");
  const [postage, setPostage] = useState(product?.postage ?? "");
  const [stock, setStock] = useState(product?.stock ?? "");
  const [active, setActive] = useState(product?.active ?? true);
  const [imageFile, setImageFile] = useState(null);
  const [benefits, setBenefits] = useState((product?.benefits || []).join("\n"));
  const [quranRefs, setQuranRefs] = useState(product?.quran_refs || []);
  const [hadithRefs, setHadithRefs] = useState(product?.hadith_refs || []);
  const [studies, setStudies] = useState(product?.modern_studies || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const addRef = (setter, blank) => setter((arr) => [...arr, blank]);
  const removeRef = (setter, idx) => setter((arr) => arr.filter((_, i) => i !== idx));
  const updateRef = (setter, idx, field, value) =>
    setter((arr) => arr.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const save = async () => {
    if (!name.trim() || price === "" || isNaN(Number(price))) { setError("Name and a numeric price are required."); return; }
    setBusy(true); setError("");
    try {
      const payload = {
        name: name.trim(), arabic_name: arabic.trim() || null, category: category.trim() || null,
        description: description.trim() || null, price: Number(price), postage: Number(postage) || 0,
        stock: stock === "" ? null : Number(stock), active,
        benefits: benefits.split("\n").map((b) => b.trim()).filter(Boolean),
        // Strip incomplete entries (need at least surah+reference for verses, collection+reference for hadiths, title for studies)
        quran_refs: quranRefs.filter((q) => q.surah && q.reference && q.arabic),
        hadith_refs: hadithRefs.filter((h) => h.collection && h.reference && h.arabic),
        modern_studies: studies.filter((s) => s.title),
      };
      let result;
      if (product) result = await adminShopUpdate(product.id, payload); else result = await adminShopCreate(payload);
      const id = result.product?.id || product?.id;
      if (imageFile && id) {
        try { await adminShopUploadImage(id, imageFile); } catch (e) { console.warn("image upload failed", e); }
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed"); setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest/40 backdrop-blur-sm" onClick={() => !busy && onClose()}>
      <div data-testid="shop-product-form" className="clay-surface rounded-3xl p-8 max-w-2xl w-full relative animate-fade-in-up max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-forest"><X className="w-5 h-5" /></button>
        <h3 className="font-heading text-2xl text-forest font-semibold mb-1">{product ? "Edit product" : "New product"}</h3>
        <p className="text-xs text-mute mb-5">Fill in the basics, then add the scholarly references that back this product up.</p>
        {error && <p className="text-xs text-clay mb-3 bg-clay/10 rounded-lg px-3 py-2">{error}</p>}

        {/* ---- Basics ---- */}
        <Field label="Product name *" value={name} onChange={setName} testid="shop-form-name" />
        <Field label="Arabic name (optional)" value={arabic} onChange={setArabic} testid="shop-form-arabic" />
        <Field label="Category (e.g. honey, oil, herb, seed)" value={category} onChange={setCategory} testid="shop-form-category" />
        <Field label="Short description" value={description} onChange={setDescription} multiline testid="shop-form-description" />
        <Field label="Health benefits (one per line)" value={benefits} onChange={setBenefits} multiline testid="shop-form-benefits" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price (£) — set 0 for free" type="number" value={price} onChange={setPrice} testid="shop-form-price" />
          <Field label="Postage (£)" type="number" value={postage} onChange={setPostage} testid="shop-form-postage" />
        </div>
        <p className="text-[10px] text-mute -mt-1 mb-3">💡 Tip: set price to <strong>0</strong> to mark this product FREE — customers will just leave their email instead of paying.</p>
        <Field label="Stock (optional)" type="number" value={stock} onChange={setStock} testid="shop-form-stock" />

        <label className="block mb-3">
          <span className="text-xs uppercase tracking-widest text-gold">Image (PNG/JPG)</span>
          <input data-testid="shop-form-image" type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            className="w-full mt-1 bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm" />
          {product?.image_url && !imageFile && <p className="text-[10px] text-mute mt-1">Current image will be kept unless you choose a new one.</p>}
        </label>

        {/* ---- Quran refs ---- */}
        <RefSection
          title="Quranic verses"
          subtitle="Include the full ayah, surah name + verse number, and English translation."
          items={quranRefs}
          onAdd={() => addRef(setQuranRefs, { surah: "", reference: "", arabic: "", translation: "" })}
          onRemove={(i) => removeRef(setQuranRefs, i)}
          fields={[
            { key: "surah", label: "Surah name", placeholder: "e.g. An-Nahl" },
            { key: "reference", label: "Verse ref", placeholder: "e.g. 16:69" },
            { key: "arabic", label: "Arabic (full ayah)", multiline: true, placeholder: "ثم كلي من كل الثمرات..." },
            { key: "translation", label: "English translation", multiline: true, placeholder: "Then eat from all the fruits..." },
          ]}
          onChange={(i, k, v) => updateRef(setQuranRefs, i, k, v)}
          testidPrefix="quran"
        />

        {/* ---- Hadith refs ---- */}
        <RefSection
          title="Authentic Hadiths"
          subtitle="Include the full hadith Arabic, English translation, collection (e.g. Sahih al-Bukhari), reference number, narrator, and grading."
          items={hadithRefs}
          onAdd={() => addRef(setHadithRefs, { collection: "", reference: "", grading: "Sahih", narrator: "", arabic: "", english: "" })}
          onRemove={(i) => removeRef(setHadithRefs, i)}
          fields={[
            { key: "collection", label: "Collection", placeholder: "Sahih al-Bukhari" },
            { key: "reference", label: "Number", placeholder: "5684" },
            { key: "grading", label: "Grading", placeholder: "Sahih" },
            { key: "narrator", label: "Narrator", placeholder: "Abu Hurayrah (RA)" },
            { key: "arabic", label: "Arabic (full hadith)", multiline: true, placeholder: "جاء رجل إلى النبي ﷺ..." },
            { key: "english", label: "English translation", multiline: true, placeholder: "A man came to the Prophet ﷺ..." },
          ]}
          onChange={(i, k, v) => updateRef(setHadithRefs, i, k, v)}
          testidPrefix="hadith"
        />

        {/* ---- Modern studies ---- */}
        <RefSection
          title="Modern medical studies"
          subtitle="Peer-reviewed research backing the product (PubMed, NCBI, university journals). Include all citation details."
          items={studies}
          onAdd={() => addRef(setStudies, { title: "", authors: "", journal: "", year: "", doi: "", finding: "" })}
          onRemove={(i) => removeRef(setStudies, i)}
          fields={[
            { key: "title", label: "Study title", placeholder: "e.g. Antibacterial activity of Sidr honey..." },
            { key: "authors", label: "Authors", placeholder: "Smith J, Khan A, et al." },
            { key: "journal", label: "Journal", placeholder: "J Ethnopharmacol" },
            { key: "year", label: "Year", placeholder: "2023" },
            { key: "doi", label: "DOI / URL", placeholder: "10.1016/j.jep.2023.116..." },
            { key: "finding", label: "Key finding", multiline: true, placeholder: "Showed 3x antibacterial activity vs manuka honey..." },
          ]}
          onChange={(i, k, v) => updateRef(setStudies, i, k, v)}
          testidPrefix="study"
        />

        <label className="inline-flex items-center gap-2 mb-5 cursor-pointer mt-4">
          <input data-testid="shop-form-active" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-forest" />
          <span className="text-xs text-mute">Visible on the public shop page</span>
        </label>

        <button data-testid="shop-form-save" onClick={save} disabled={busy}
          className="w-full bg-forest text-sand hover:bg-sage rounded-full py-3 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save product
        </button>
      </div>
    </div>
  );
};

const RefSection = ({ title, subtitle, items, fields, onAdd, onRemove, onChange, testidPrefix }) => (
  <div className="border-t border-forest/10 mt-5 pt-5">
    <div className="flex items-start justify-between gap-3 mb-2">
      <div>
        <h4 className="font-heading text-base text-forest font-semibold">{title}</h4>
        <p className="text-[11px] text-mute leading-snug max-w-md">{subtitle}</p>
      </div>
      <button type="button" data-testid={`add-${testidPrefix}`} onClick={onAdd}
        className="inline-flex items-center gap-1 text-[11px] bg-forest text-sand hover:bg-sage rounded-full px-3 py-1.5">
        <Plus className="w-3 h-3" /> Add
      </button>
    </div>
    {items.length === 0 && <p className="text-[11px] text-mute italic mb-2">None yet.</p>}
    {items.map((it, idx) => (
      <div key={idx} data-testid={`${testidPrefix}-row-${idx}`} className="bg-cream rounded-xl p-3 mb-2 relative">
        <button type="button" data-testid={`remove-${testidPrefix}-${idx}`} onClick={() => onRemove(idx)}
          className="absolute top-2 right-2 text-clay hover:text-clay/70" title="Remove">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <div className="grid grid-cols-2 gap-2 pr-7">
          {fields.map((f) => (
            <div key={f.key} className={f.multiline ? "col-span-2" : ""}>
              <label className="block text-[10px] uppercase tracking-widest text-gold mb-0.5">{f.label}</label>
              {f.multiline ? (
                <textarea
                  data-testid={`${testidPrefix}-${idx}-${f.key}`}
                  value={it[f.key] || ""}
                  onChange={(e) => onChange(idx, f.key, e.target.value)}
                  placeholder={f.placeholder}
                  rows={2}
                  className="w-full bg-white border border-forest/15 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-forest resize-none"
                />
              ) : (
                <input
                  data-testid={`${testidPrefix}-${idx}-${f.key}`}
                  value={it[f.key] || ""}
                  onChange={(e) => onChange(idx, f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full bg-white border border-forest/15 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-forest"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Field = ({ label, value, onChange, type = "text", multiline, testid }) => (
  <label className="block mb-3">
    <span className="text-xs uppercase tracking-widest text-gold">{label}</span>
    {multiline ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full mt-1 bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm outline-none focus:border-forest resize-none" />
    ) : (
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)}
        className="w-full mt-1 bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm outline-none focus:border-forest" />
    )}
  </label>
);

// =========================== Tutors tab ===============================
const authHdr = () => ({ Authorization: `Bearer ${getAdminToken()}` });

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const COMMON_TZ = ["Africa/Cairo", "Asia/Riyadh", "Asia/Dubai", "Europe/London", "Europe/Istanbul", "America/New_York", "Asia/Karachi"];

const ActiveTutorRow = ({ tutor: t, busy, onAction, reload }) => {
  const [slots, setSlots] = useState([]);
  const [showSlots, setShowSlots] = useState(false);
  const [newWeekday, setNewWeekday] = useState(0);
  const [newTime, setNewTime] = useState("14:00");
  const [newTz, setNewTz] = useState("Africa/Cairo");

  const loadSlots = async () => {
    try {
      const r = await axios.get(`${API}/tutoring/tutors/${t.id}/slots`);
      setSlots(r.data.slots || []);
    } catch (e) { console.warn("slots load failed", e); }
  };
  useEffect(() => { if (showSlots) loadSlots(); /* eslint-disable-next-line */ }, [showSlots]);

  const addSlot = async () => {
    try {
      await axios.post(`${API}/tutoring/tutors/${t.id}/slots`,
        { weekday: newWeekday, local_time: newTime, tz: newTz },
        { headers: authHdr() });
      await loadSlots();
    } catch (e) { alert("Failed: " + (e?.response?.data?.detail || e.message)); }
  };
  const removeSlot = async (slotId) => {
    if (!window.confirm("Remove this slot?")) return;
    try {
      await axios.delete(`${API}/tutoring/tutors/${t.id}/slots/${slotId}`, { headers: authHdr() });
      await loadSlots();
    } catch (e) { alert("Failed: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div data-testid={`active-${t.id}`} className="border border-forest/10 rounded-2xl p-4">
      <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
        <div>
          <p className="font-semibold text-forest">{t.full_name}</p>
          <p className="text-xs text-mute">{t.country} · {t.email}</p>
          <p className="text-xs text-gold">{t.paid_subscriptions || 0} paid subscription(s)</p>
        </div>
        <div className="flex gap-2">
          <button data-testid={`slots-toggle-${t.id}`} onClick={() => setShowSlots(!showSlots)}
            className="text-xs inline-flex items-center gap-1 bg-cream border border-forest/20 text-forest px-3 py-1.5 rounded-full hover:bg-forest/5">
            <Calendar className="w-3 h-3" /> {showSlots ? "Hide" : "Slots"}
          </button>
          <button data-testid={`deactivate-${t.id}`}
            onClick={() => onAction(`Deactivate ${t.full_name}`, `${API}/tutoring/admin/tutors/${t.id}/deactivate`)}
            className="text-xs inline-flex items-center gap-1 bg-clay/10 text-clay px-3 py-1.5 rounded-full hover:bg-clay/20">
            <UserMinus className="w-3 h-3" /> Deactivate
          </button>
        </div>
      </div>

      {showSlots && (
        <div data-testid={`slots-${t.id}`} className="border-t border-forest/10 mt-3 pt-3">
          <p className="text-[10px] uppercase tracking-widest text-gold mb-2">Weekly slots (tutor's local time)</p>
          {slots.length === 0 && <p className="text-xs text-mute mb-2">No slots yet — add one below.</p>}
          <div className="flex flex-wrap gap-2 mb-3">
            {slots.map((s) => (
              <span key={s.id} className="text-xs inline-flex items-center gap-1 bg-cream border border-forest/15 px-3 py-1 rounded-full">
                {DAYS[s.weekday]} {s.local_time} · <span className="text-mute">{s.tz}</span>
                <button data-testid={`slot-del-${s.id}`} onClick={() => removeSlot(s.id)} className="text-clay ml-1"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs">
              Day
              <select value={newWeekday} onChange={(e) => setNewWeekday(parseInt(e.target.value, 10))}
                className="block mt-1 px-3 py-1.5 rounded-full bg-cream border border-forest/10 text-xs">
                {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </label>
            <label className="text-xs">
              Time
              <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)}
                className="block mt-1 px-3 py-1.5 rounded-full bg-cream border border-forest/10 text-xs" />
            </label>
            <label className="text-xs">
              Timezone
              <select value={newTz} onChange={(e) => setNewTz(e.target.value)}
                className="block mt-1 px-3 py-1.5 rounded-full bg-cream border border-forest/10 text-xs">
                {COMMON_TZ.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </label>
            <button data-testid={`slot-add-${t.id}`} onClick={addSlot}
              className="text-xs inline-flex items-center gap-1 bg-forest text-sand px-3 py-1.5 rounded-full hover:bg-sage">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TutorsTab = () => {
  const [tutors, setTutors] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [audio, setAudio] = useState({});         // tutor_id -> data URL for playback

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/tutoring/admin/tutors`, { headers: authHdr() }),
        axios.get(`${API}/tutoring/admin/sessions`, { headers: authHdr() }),
      ]);
      setTutors(t.data.tutors || []);
      setSessions(s.data.sessions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load tutors");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const listenTo = async (tutorId) => {
    if (audio[tutorId]) { setAudio((cur) => ({ ...cur, [tutorId]: null })); return; }
    setBusy(`audio-${tutorId}`);
    try {
      const r = await axios.get(`${API}/tutoring/admin/tutors/${tutorId}/recitation`, { headers: authHdr() });
      const b64 = r.data.audio_b64;
      setAudio((cur) => ({ ...cur, [tutorId]: `data:audio/webm;base64,${b64}` }));
    } catch (e) { alert("Failed to load audio: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(null); }
  };

  const doAction = async (label, url, body = null, method = "post") => {
    if (!window.confirm(label + "?")) return;
    setBusy(url);
    try {
      const cfg = { headers: authHdr() };
      if (method === "post") await axios.post(url, body || {}, cfg);
      else await axios.delete(url, cfg);
      await load();
    } catch (e) { alert("Failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(null); }
  };

  const reassign = async (session_id) => {
    const approved = tutors.filter((t) => t.approved);
    if (!approved.length) return alert("No approved tutors available.");
    const options = approved.map((t, i) => `${i + 1}. ${t.full_name} (${t.country})`).join("\n");
    const pick = window.prompt(`Reassign session ${session_id.slice(-8)} to:\n${options}\n\nType the number:`);
    const idx = parseInt(pick, 10) - 1;
    if (isNaN(idx) || !approved[idx]) return;
    const reason = window.prompt("Reason (visible to admin only):", "Original tutor unavailable — emergency reassignment");
    if (!reason || reason.length < 3) return;
    await doAction(
      `Reassign to ${approved[idx].full_name}`,
      `${API}/tutoring/admin/sessions/${session_id}/reassign`,
      { new_tutor_id: approved[idx].id, reason },
    );
  };

  const refund = async (session_id) => {
    const reason = window.prompt("Refund reason (min 5 chars):");
    if (!reason || reason.length < 5) return;
    await doAction("Refund this session via Stripe",
      `${API}/tutoring/sessions/${session_id}/refund`,
      { reason });
  };

  const pending = tutors.filter((t) => !t.approved && !t.deactivated_at);
  const active = tutors.filter((t) => t.approved);
  const deactivated = tutors.filter((t) => !t.approved && t.deactivated_at);

  const paidSessions = sessions.filter((s) => s.payment_status === "paid");
  const payoutByTutor = paidSessions.reduce((acc, s) => {
    acc[s.tutor_id] = acc[s.tutor_id] || { name: s.tutor_name, count: 0 };
    acc[s.tutor_id].count += 1;
    return acc;
  }, {});

  if (loading) return <div className="py-16 text-center text-mute"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-8" data-testid="tutors-tab">
      {error && <div className="rounded-2xl bg-clay/10 border border-clay/30 text-clay text-sm px-4 py-3 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>}

      {/* Pending applications */}
      <section className="bg-white rounded-3xl border border-gold/20 p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-gold" />
          <h2 className="font-heading text-lg text-forest font-semibold">Pending applications ({pending.length})</h2>
        </div>
        {pending.length === 0 ? <p className="text-sm text-mute">No pending applications.</p> : (
          <div className="space-y-3">
            {pending.map((t) => (
              <div key={t.id} data-testid={`pending-${t.id}`} className="border border-forest/10 rounded-2xl p-4">
                <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-forest">{t.full_name} <span className="text-xs text-mute font-normal">· {t.country}</span></p>
                    <p className="text-xs text-mute">{t.email} · {t.phone}</p>
                    <p className="text-xs text-mute">Ijāza: {t.ijaza || "—"} · {t.experience_years} yrs · {(t.specialities || []).join(", ")}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid={`listen-${t.id}`} disabled={busy === `audio-${t.id}` || !t.has_recitation}
                      onClick={() => listenTo(t.id)}
                      className="text-xs inline-flex items-center gap-1 bg-gold/15 text-gold px-3 py-1.5 rounded-full hover:bg-gold/25 disabled:opacity-40">
                      <Mic className="w-3 h-3" /> {audio[t.id] ? "Hide audio" : "Listen"}
                    </button>
                    <button data-testid={`approve-${t.id}`}
                      onClick={() => doAction(`Approve ${t.full_name}`, `${API}/tutoring/admin/tutors/${t.id}/approve`)}
                      className="text-xs inline-flex items-center gap-1 bg-forest text-sand px-3 py-1.5 rounded-full hover:bg-sage">
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </button>
                    <button data-testid={`reject-${t.id}`}
                      onClick={() => doAction(`Reject & delete ${t.full_name}`, `${API}/tutoring/admin/tutors/${t.id}/reject`)}
                      className="text-xs inline-flex items-center gap-1 bg-clay/10 text-clay px-3 py-1.5 rounded-full hover:bg-clay/20">
                      <Trash2 className="w-3 h-3" /> Reject
                    </button>
                  </div>
                </div>
                <p className="text-xs text-mute italic mt-2 whitespace-pre-line">{t.bio}</p>
                {audio[t.id] && <audio src={audio[t.id]} controls className="w-full mt-3" data-testid={`audio-player-${t.id}`} />}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Active tutors */}
      <section className="bg-white rounded-3xl border border-forest/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap className="w-5 h-5 text-forest" />
          <h2 className="font-heading text-lg text-forest font-semibold">Active tutors ({active.length})</h2>
        </div>
        {active.length === 0 ? <p className="text-sm text-mute">No active tutors yet.</p> : (
          <div className="grid gap-3">
            {active.map((t) => (
              <ActiveTutorRow key={t.id} tutor={t} busy={busy} onAction={doAction} reload={load} />
            ))}
          </div>
        )}
      </section>

      {/* Sessions + refund + reassignment */}
      <section className="bg-white rounded-3xl border border-forest/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Video className="w-5 h-5 text-forest" />
          <h2 className="font-heading text-lg text-forest font-semibold">Tutor sessions ({sessions.length})</h2>
        </div>
        {sessions.length === 0 ? <p className="text-sm text-mute">No sessions yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-mute">
                <tr className="border-b border-forest/10">
                  <th className="text-left py-2">Session</th>
                  <th className="text-left py-2">Student</th>
                  <th className="text-left py-2">Tutor</th>
                  <th className="text-left py-2">Plan</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} data-testid={`session-${s.id}`} className="border-b border-forest/5">
                    <td className="py-2 font-mono text-[10px] text-mute">{s.id.slice(-8)}</td>
                    <td className="py-2">{s.student_name}<br/><span className="text-[10px] text-mute">{s.student_email}</span></td>
                    <td className="py-2">{s.tutor_name}</td>
                    <td className="py-2">{s.plan} · £{s.fee_gbp}</td>
                    <td className="py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        s.status === "refunded" ? "bg-clay/10 text-clay" :
                        s.payment_status === "paid" ? "bg-forest/10 text-forest" : "bg-gold/15 text-gold"
                      }`}>{s.status || s.payment_status}</span>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {s.payment_status === "paid" && s.status !== "refunded" && (
                        <>
                          <button data-testid={`reassign-${s.id}`} onClick={() => reassign(s.id)}
                            className="text-[11px] inline-flex items-center gap-1 bg-cream border border-forest/20 text-forest px-2 py-1 rounded-full mr-1 hover:bg-forest/5">
                            <ArrowLeftRight className="w-3 h-3" /> Reassign
                          </button>
                          <button data-testid={`refund-${s.id}`} onClick={() => refund(s.id)}
                            className="text-[11px] inline-flex items-center gap-1 bg-clay/10 text-clay px-2 py-1 rounded-full hover:bg-clay/20">
                            <BadgePoundSterling className="w-3 h-3" /> Refund
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Payout tally */}
      <section className="bg-white rounded-3xl border border-gold/20 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BadgePoundSterling className="w-5 h-5 text-gold" />
          <h2 className="font-heading text-lg text-forest font-semibold">Payout tally (paid subscriptions per tutor)</h2>
        </div>
        {Object.keys(payoutByTutor).length === 0 ? <p className="text-sm text-mute">No paid sessions yet.</p> : (
          <ul className="text-sm space-y-1">
            {Object.entries(payoutByTutor).map(([id, p]) => (
              <li key={id} className="flex items-center justify-between border-b border-forest/5 py-1.5">
                <span>{p.name} <span className="text-[10px] text-mute font-mono">{id.slice(-6)}</span></span>
                <span className="text-gold font-semibold">{p.count} paid</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Deactivated tutors */}
      {deactivated.length > 0 && (
        <section className="bg-white rounded-3xl border border-clay/20 p-6">
          <h2 className="font-heading text-lg text-forest font-semibold mb-3">Deactivated tutors ({deactivated.length})</h2>
          <ul className="text-sm space-y-2">
            {deactivated.map((t) => (
              <li key={t.id} className="flex items-center justify-between">
                <span>{t.full_name} <span className="text-[10px] text-mute">· deactivated {t.deactivated_at?.slice(0, 10)}</span></span>
                <button data-testid={`reactivate-${t.id}`}
                  onClick={() => doAction(`Reactivate ${t.full_name}`, `${API}/tutoring/admin/tutors/${t.id}/reactivate`)}
                  className="text-xs inline-flex items-center gap-1 bg-forest/10 text-forest px-3 py-1.5 rounded-full hover:bg-forest/20">
                  <UserPlus className="w-3 h-3" /> Reactivate
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

// =========================== Notifications (delivery) tab ===============================
const STATUS_STYLE = {
  delivered: "bg-forest/15 text-forest", read: "bg-forest/15 text-forest",
  sent: "bg-sage/20 text-forest", queued: "bg-gold/15 text-gold", sending: "bg-gold/15 text-gold",
  accepted: "bg-gold/15 text-gold", undelivered: "bg-clay/15 text-clay",
  failed: "bg-clay/20 text-clay", skipped: "bg-mute/15 text-mute",
};

const NotificationsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await adminNotificationLogs()); }
    catch (e) { setData({ logs: [], tally: {} }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const mask = (to) => {
    if (!to) return "—";
    const v = String(to).replace("whatsapp:", "");
    return v.includes("@") ? v : v;
  };

  return (
    <div data-testid="notifications-tab" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl text-forest font-semibold">Notification delivery</h2>
          <p className="text-xs text-mute">Live WhatsApp &amp; email delivery status for bookings, reminders and alerts.</p>
        </div>
        <button data-testid="notifications-refresh" onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-forest hover:bg-forest/5 rounded-full px-3 py-2 transition">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-2" data-testid="notifications-tally">
          {Object.entries(data.tally || {}).map(([st, n]) => (
            <span key={st} className={`text-[11px] px-3 py-1.5 rounded-full capitalize ${STATUS_STYLE[st] || "bg-mute/15 text-mute"}`}>
              {st}: {n}
            </span>
          ))}
        </div>
      )}

      {loading && !data ? (
        <div className="py-16 text-center text-mute"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <div className="clay-surface rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 text-mute text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Channel</th>
                  <th className="text-left px-4 py-3">Recipient</th>
                  <th className="text-left px-4 py-3">Message</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {(data?.logs || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-mute italic">No notifications sent yet.</td></tr>
                )}
                {(data?.logs || []).map((l, i) => (
                  <tr key={i} data-testid="notification-log-row" className="border-t border-forest/5">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        {l.channel === "whatsapp" ? <Phone className="w-3.5 h-3.5 text-forest" /> : <Mail className="w-3.5 h-3.5 text-gold" />}
                        {l.channel}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink">{mask(l.to)}</td>
                    <td className="px-4 py-3 text-xs text-mute max-w-[280px] truncate" title={l.label}>{l.label}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${STATUS_STYLE[l.status] || "bg-mute/15 text-mute"}`}>{l.status}</span>
                      {l.error && <span className="block text-[10px] text-clay mt-1 max-w-[220px] truncate" title={l.error}>{l.error}</span>}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-mute whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


// =========================== Main Dashboard wrapper ===========================
const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    setLoading(true); setError("");
    try { const d = await adminDashboard(); setData(d); }
    catch (e) {
      if (e?.response?.status === 401) { clearAdminToken(); onLogout(); return; }
      setError(e?.response?.data?.detail || e.message || "Failed to load dashboard");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const handleMarkSent = async (session_id) => {
    if (!window.confirm("Mark this diploma certificate as signed & sent to the buyer?")) return;
    setBusy(session_id);
    try { await adminMarkCertificateSent(session_id); await refresh(); }
    catch (e) { alert(e?.response?.data?.detail || e.message || "Failed"); }
    finally { setBusy(null); }
  };

  const handleRefund = async (order) => {
    const reason = window.prompt(
      `Refund ${fmtMoney(order.amount, (order.currency || "gbp").toUpperCase())} to ${order.full_name || order.email || "this customer"}?\n\nThis sends the money back via Stripe immediately.\nOptional reason (leave blank to skip):`,
      ""
    );
    if (reason === null) return; // cancelled
    setBusy(`refund-${order.session_id}`);
    try {
      const r = await adminRefundOrder(order.session_id, reason || "");
      alert(r?.already ? "This order was already refunded." : "Refund issued successfully via Stripe.");
      await refresh();
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Refund failed"); }
    finally { setBusy(null); }
  };

  const handleMarkDelivered = async (order) => {
    if (!window.confirm(`Mark this order as delivered to ${order.full_name || order.email || "the customer"}?`)) return;
    setBusy(`deliver-${order.session_id}`);
    try {
      await adminMarkDelivered(order.session_id);
      await refresh();
    } catch (e) { alert(e?.response?.data?.detail || e.message || "Could not update"); }
    finally { setBusy(null); }
  };

  const handleDownloadCert = async (session_id) => {
    setBusy(`dl-${session_id}`);
    try {
      const res = await fetch(`${API}/admin/certificate/${session_id}/preview`, { headers: { Authorization: `Bearer ${getAdminToken()}` } });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `unsigned-cert-${session_id.slice(-8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert("Download failed: " + (e.message || e)); }
    finally { setBusy(null); }
  };

  if (loading && !data) return <div className="min-h-screen flex items-center justify-center text-mute"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand">
      <AdminBar tab={tab} setTab={setTab} onLogout={() => { clearAdminToken(); onLogout(); }} onRefresh={refresh} loading={loading} />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {error && <div className="mb-6 rounded-2xl bg-clay/10 border border-clay/30 text-clay text-sm px-4 py-3 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>}
        {tab === "overview" && <OverviewTab data={data} busy={busy} onMarkSent={handleMarkSent} onDownloadCert={handleDownloadCert} onRefund={handleRefund} onMarkDelivered={handleMarkDelivered} />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "exams" && <ExamsTab />}
        {tab === "shop" && <ShopTab />}
        {tab === "tutors" && <TutorsTab />}
        {tab === "sessions" && <SessionsTab />}
        {tab === "notifications" && <NotificationsTab />}
      </div>
    </div>
  );
};

// =========================== Wrapper ===========================
const Admin = () => {
  const [authed, setAuthed] = useState(!!getAdminToken());
  if (!authed) return <AdminLogin onLoggedIn={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
};

export default Admin;
